import './bootstrap'
import '../css/app.css'
import 'floating-vue/dist/style.css'
import '../css/popper.css'

import { createApp, h } from 'vue'
import { createPinia } from 'pinia'
import { createSentryPiniaPlugin } from "@sentry/vue"

import router from './Router'

import FloatingVue from 'floating-vue'

import 'v-onboarding/dist/style.css'

import PrimeVue from 'primevue/config'
import { definePreset, palette } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import 'primeicons/primeicons.css'

import * as Sentry from '@sentry/vue'
// import VueGtag from 'vue-gtag'

import App from './App.vue'

const app = createApp(App)

const pinia = createPinia()

const appName = import.meta.env.VITE_APP_NAME || 'LifeWorx Latte'

const FloatingVueConfig = {
    themes: {
      'tooltip': {
        delay: {
            show: 50,
            hide: 0,
        },
        overflowPadding: 90, // Account for navbar/module header height
      },
      'tooltip-nav': {
        '$extend': 'tooltip',
        triggers: ['hover'],
        autoHide: true,
        placement: 'bottom',
      },
      'lifeworx': {
        '$extend': 'dropdown',
        triggers: ['click'],
        autoHide: true,
        placement: 'bottom',
      },
      'lifeworx-red-outline': {
        '$extend': 'dropdown',
        triggers: ['click'],
        autoHide: true,
        placement: 'top',
      },
      'lifeworx-yellow-outline': {
        '$extend': 'dropdown',
        triggers: ['click'],
        autoHide: true,
        placement: 'top',
      },
      'lifeworx-green-outline': {
        '$extend': 'dropdown',
        triggers: ['click'],
        autoHide: true,
        placement: 'top',
      },
      'lifeworx-schedule-blue': {
        '$extend': 'dropdown',
        triggers: ['hover'],
        autoHide: false,
        placement: 'top',
      },
      'lifeworx-schedule-red': {
        '$extend': 'dropdown',
        triggers: ['hover'],
        autoHide: false,
        placement: 'top',
      },
      'lifeworx-schedule-gray': {
        '$extend': 'dropdown',
        triggers: ['hover'],
        autoHide: false,
        placement: 'top',
      },
    },
}

const userContext = JSON.parse(document.getElementById('app').getAttribute('data-user'))
const sentryUser = {
    email: userContext?.user_id ? userContext.email : null,
    name: userContext?.user_id ? userContext.first_name + ' ' + userContext.last_name : null
}

if (import.meta.env.VITE_SENTRY_DSN_PUBLIC) {
    Sentry.init({
        app,
        debug: import.meta.env.VITE_APP_ENVIRONMENT === 'local' && import.meta.env.VITE_SENTRY_DEBUG === 'true',
        trackComponents: true,
        environment: import.meta.env.VITE_APP_ENVIRONMENT,
        dsn: import.meta.env.VITE_SENTRY_DSN_PUBLIC,
        ignoreErrors: [
            /\[VueSlider error\]: The "value" must be greater than or equal to the "min"\./,
            "google.maps.places.AutocompleteService is not available to new customers",
            "google.maps.places.PlacesService is not available to new customers",
            "store installed",
            "[vite]"
        ],
        beforeSend(event, hint) {
            if (event.exception && event.event_id) {
                const feedback = import.meta.env.VITE_SENTRY_ENABLE_CRASH_FEEDBACK === true ? Sentry.getFeedback() : null
                feedback?.attachTo(document.querySelector("#app"), {
                    formTitle: 'Something Went Wrong!',
                    submitButtonLabel: 'Send Report',
                    messageLabel: '',
                    messagePlaceholder: 'If you encountered a bug, what was it? What did you expect? Add a screenshot and share as much detail as possible.',
                    addScreenshotButtonLabel: 'Add a Screenshot',
                    successMessageText: 'Thanks for your report. Reloading Latte...',
                    isRequiredLabel: '',
                    onSubmitSuccess: (data) => {
                        setTimeout(() => {
                            location.reload()
                        }, 5000)
                    },
                })
            }
            return event;
        },
        integrations: [
            Sentry.feedbackIntegration({
                colorScheme: 'dark',
                autoInject: false,
                showBranding: false,
                showName: false,
                showEmail: false,
                useSentryUser: {
                    email: 'email',
                    name: 'name',
                },
                triggerLabel: '',
                formTitle: 'Share Your Feedback',
                submitButtonLabel: 'Send Feedback',
                messageLabel: '',
                messagePlaceholder: 'If you encountered a bug, what was it? What did you expect? Add a screenshot and share as much detail as possible.',
                addScreenshotButtonLabel: 'Add a Screenshot',
                isRequiredLabel: ''
            }),
            Sentry.browserTracingIntegration({ router }),
            Sentry.captureConsoleIntegration(),
            Sentry.replayIntegration(),
        ],
        // Performance Monitoring
        tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
    Sentry.setUser(sentryUser)
}

app.use(pinia)

app.use(router)

app.use(FloatingVue, FloatingVueConfig)

const LwxPreset = definePreset(Aura, {
    semantic: {
        // Primary brand color - LifeWorx Blue
        primary: {
            50: '#78E5FF',
            100: '#85d7ed',
            150: '#4FDDFF',
            200: '#26D5FF',
            300: '#00CCFD',
            400: '#00ABD4',
            500: '#008AAB',
            600: '#005D73',
            700: '#002F3B',
            800: '#001F2B',
            900: '#000203',
            950: '#000000'
        },
        // Accent colors for secondary actions
        accent: {
            50: '#fdf6ef',
            100: '#f9ebdb',
            150: '#f3d3b5',
            200: '#eab687',
            300: '#e18f56',
            400: '#dc783e',
            500: '#cc5a2a',
            600: '#a94525',
            700: '#883924',
            800: '#6e3120',
            900: '#3b170f',
            950: '#2a0f08'
        },
        // Success state - LifeWorx Green
        success: {
            50: '#C8F2B5',
            100: '#B0E798',
            150: '#acc9a7',
            200: '#9BDD7E',
            300: '#80C95E',
            400: '#5CAA39',
            500: '#3F911A',
            600: '#347715',
            700: '#285D10',
            800: '#1D430C',
            900: '#122907',
            950: '#0C1C05'
        },
        // Warning state - LifeWorx Yellow
        warn: {
            50: '#FDEEB2',
            100: '#FDEAA1',
            150: '#fce37e',
            200: '#FCE37E',
            300: '#FBDB5B',
            400: '#FBD438',
            500: '#FACC15',
            600: '#D7AD05',
            700: '#A58504',
            800: '#745D02',
            900: '#423501',
            950: '#292101'
        },
        // Error/Danger state - LifeWorx Red
        error: {
            50: '#FFDED6',
            100: '#FFC3B5',
            150: '#f4957e',
            200: '#F4957E',
            300: '#E66849',
            400: '#CC4221',
            500: '#AB2100',
            600: '#8C1B00',
            700: '#6E1500',
            800: '#4F0F00',
            900: '#310900',
            950: '#210600'
        },
        // Purple accent for additional brand color
        purple: {
            50: '#ECBFFF',
            100: '#E2A1FF',
            150: '#bc9dc9',
            200: '#D064FF',
            300: '#BD26FF',
            400: '#A200E8',
            500: '#7700AB',
            600: '#62008C',
            700: '#4C006E',
            800: '#37004F',
            900: '#220031',
            950: '#170021'
        },
        // Focus ring styling
        focusRing: {
            width: '2px',
            style: 'solid',
            color: '{primary.color}',
            offset: '0px',
            shadow: 'none'
        }
    }
})

app.use(PrimeVue, {
    theme: {
        preset: LwxPreset,
        options: {
            darkModeSelector: '.dark-mode',
            cssLayer: {
                name: 'primevue',
                order: 'tailwind-base, primevue, tailwind-utilities',
            },
        }
    }
})

// app.use(VueGtag, {
//     config: {
//         id: import.meta.env.VITE_GA_MEASUREMENT_ID,
//     }
// }, router)

app.mount('#app')
